// Users Management – taskJobs/projects-style redesign
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import EditUserModal from "./EditUserModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import CreateUserModal from "./CreateUserModal";
import { getUsersList, deleteUser } from "api/apiService";
import { notifyCenter } from "utils/toast";

// Role → MUI chip color mapping
const ROLE_COLOR = {
  admin:        "error",
  admin_staff:  "warning",
  vendor:       "info",
  vendor_staff: "info",
  customer:     "default",
};

// Color the avatar deterministically from the name/email string
function stringToColor(str = "") {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const v = (hash >> (i * 8)) & 0xff;
    color += `00${v.toString(16)}`.slice(-2);
  }
  return color;
}

function getInitials(name = "") {
  const [f = "", l = ""] = String(name).trim().split(/\s+/);
  return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || "?";
}

function NameCell({ name, email }) {
  const seed = name || email || "user";
  return (
    <SoftBox display="flex" alignItems="center" gap={1.5}>
      <Avatar
        variant="rounded"
        sx={{
          width: 36,
          height: 36,
          fontSize: 13,
          fontWeight: 700,
          bgcolor: stringToColor(seed),
          color: "#fff",
          borderRadius: "8px",
        }}
      >
        {getInitials(name)}
      </Avatar>
      <SoftBox display="flex" flexDirection="column" sx={{ minWidth: 0 }}>
        <SoftTypography variant="button" fontWeight="medium" sx={{ textTransform: "none", fontSize: 13 }}>
          {name || "—"}
        </SoftTypography>
        <SoftTypography variant="caption" color="secondary" sx={{ fontSize: 11 }}>
          {email || ""}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}
NameCell.propTypes = { name: PropTypes.string, email: PropTypes.string };
NameCell.defaultProps = { name: "", email: "" };

export default function UserMangemntTable() {
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  async function loadPage(p = page) {
    setLoading(true);
    setError(null);
    try {
      const res = await getUsersList({ limit: pageSize, page_number: p });
      if (res?.status) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setTotalRecords(res?.data_info?.total_records ?? 0);
        setTotalPages(res?.data_info?.total_pages ?? 1);
      } else {
        setError(res?.message || "Kunne ikke hente brukere");
        setRows([]);
      }
    } catch (e) {
      setError(e?.message || "Nettverksfeil");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q) ||
        (u.address || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const offset = (page - 1) * pageSize;

  function onEditRow(user) {
    setEditingId(user?.id);
    setOpenEdit(true);
  }

  async function handleEdited() {
    setOpenEdit(false);
    setEditingId(null);
    await loadPage(page);
    notifyCenter("success", "Brukeren ble oppdatert.", { duration: 1600 });
  }

  function handleAskDelete(id) {
    setDeleteId(id);
    setConfirmOpen(true);
  }

  async function handleConfirmDelete() {
    setConfirmOpen(false);
    if (!deleteId) return;
    setLoading(true);
    try {
      const res = await deleteUser(deleteId);
      if (res?.status) {
        notifyCenter("success", res.message || "Bruker slettet", { duration: 2000 });
        loadPage(page);
      } else {
        notifyCenter("error", res?.message || "Kunne ikke slette bruker", { duration: 3000 });
      }
    } catch (e) {
      notifyCenter("error", "Kunne ikke slette bruker", { duration: 3000 });
    } finally {
      setDeleteId(null);
      setLoading(false);
    }
  }

  return (
    <DashboardLayout>
      <SoftBox py={3} px={{ xs: 1, md: 2 }}>
        <Card sx={{ position: "relative" }}>
          {/* Header */}
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            p={3}
          >
            <SoftBox>
              <SoftTypography variant="h5" fontWeight="bold">
                Brukeradministrasjon
              </SoftTypography>
              <SoftTypography variant="button" color="text">
                Alle brukere, roller og kontaktinformasjon
              </SoftTypography>
            </SoftBox>
            <SoftButton
              variant="gradient"
              color="info"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setCreateOpen(true)}
            >
              Legg til bruker
            </SoftButton>
          </SoftBox>
          <Divider sx={{ my: 0 }} />

          {/* Search */}
          <SoftBox p={3}>
            <SoftBox sx={{ maxWidth: 420 }}>
              <SoftTypography variant="caption" fontWeight="bold" color="text" mb={0.5} display="block">
                Søk
              </SoftTypography>
              <SoftInput
                placeholder="Navn, e-post, rolle eller adresse"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SoftBox>
          </SoftBox>
          <Divider sx={{ my: 0 }} />

          {error && (
            <SoftBox px={3} py={2}>
              <SoftTypography variant="button" color="error">
                {error}
              </SoftTypography>
            </SoftBox>
          )}

          {/* Table */}
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                width: "100%",
                minWidth: 900,
                tableLayout: "fixed",
                "& thead": {
                  display: "table-header-group",
                  padding: 0,
                  borderRadius: 0,
                },
                "& tbody": { display: "table-row-group" },
                "& tr": { display: "table-row" },
                "& td, & th": {
                  display: "table-cell",
                  padding: "12px 14px",
                  borderBottom: (t) => `1px solid ${t.palette.grey[200]}`,
                  fontSize: 13,
                  verticalAlign: "middle",
                },
                "& thead th": {
                  backgroundColor: (t) => t.palette.grey[100],
                  color: (t) => t.palette.text.secondary,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  fontSize: 11,
                  letterSpacing: 0.4,
                  whiteSpace: "nowrap",
                },
                "& tbody tr": { cursor: "pointer" },
                "& tbody tr:hover": {
                  backgroundColor: (t) => t.palette.grey[50],
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "8%" }}>ID</TableCell>
                  <TableCell sx={{ width: "28%" }}>Navn</TableCell>
                  <TableCell sx={{ width: "14%" }}>Rolle</TableCell>
                  <TableCell sx={{ width: "auto" }}>Adresse</TableCell>
                  <TableCell sx={{ width: "10%" }} align="right">Handlinger</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && filtered.length === 0 && (
                  <TableRow sx={{ cursor: "default !important" }}>
                    <TableCell colSpan={5} sx={{ py: 6 }}>
                      <SoftBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <GroupsOutlinedIcon sx={(t) => ({ fontSize: 48, color: t.palette.grey[400] })} />
                        <SoftTypography variant="body2" color="secondary">
                          {search ? "Ingen brukere matcher søket" : "Ingen brukere enda"}
                        </SoftTypography>
                        {!search && (
                          <SoftButton
                            variant="gradient"
                            color="info"
                            size="small"
                            startIcon={<AddIcon fontSize="small" />}
                            onClick={() => setCreateOpen(true)}
                            sx={{ mt: 1 }}
                          >
                            Legg til bruker
                          </SoftButton>
                        )}
                      </SoftBox>
                    </TableCell>
                  </TableRow>
                )}

                {filtered.map((u) => (
                  <TableRow key={u.id} hover onClick={() => onEditRow(u)}>
                    <TableCell sx={{ color: "text.secondary", fontFamily: "monospace" }}>
                      #{u.id}
                    </TableCell>
                    <TableCell>
                      <NameCell name={u.name || ""} email={u.email || ""} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={u.role || "—"}
                        color={ROLE_COLOR[u.role] || "default"}
                        size="small"
                        sx={{ fontWeight: 600, height: 22 }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: (t) => t.palette.text.secondary,
                        fontSize: 12,
                      }}
                    >
                      {u.address || "—"}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ whiteSpace: "nowrap" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Rediger">
                        <IconButton size="small" onClick={() => onEditRow(u)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Slett">
                        <IconButton
                          size="small"
                          onClick={() => handleAskDelete(u.id)}
                          sx={{ color: "error.main" }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer pagination */}
          {filtered.length > 0 && (
            <>
              <Divider sx={{ my: 0 }} />
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                p={3}
              >
                <SoftTypography variant="caption" color="secondary">
                  Viser {filtered.length ? offset + 1 : 0}
                  {"–"}
                  {offset + filtered.length} av {totalRecords}
                </SoftTypography>
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || loading}
                  >
                    Forrige
                  </SoftButton>
                  <SoftTypography variant="caption" color="text" px={1}>
                    Side {page} / {totalPages}
                  </SoftTypography>
                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages || loading}
                  >
                    Neste
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </>
          )}

          {loading && (
            <SoftBox
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
                borderRadius: "inherit",
              }}
            >
              <CircularProgress color="info" size={36} />
            </SoftBox>
          )}
        </Card>
      </SoftBox>

      <ConfirmDeleteModal
        open={confirmOpen}
        title="Slette bruker?"
        message="Er du sikker på at du vil slette denne brukeren? Dette kan ikke angres."
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => loadPage(page)}
      />
      <EditUserModal
        open={openEdit}
        userId={editingId}
        onClose={() => {
          setOpenEdit(false);
          setEditingId(null);
        }}
        onSaved={handleEdited}
      />
    </DashboardLayout>
  );
}
